import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { Sparkles, Loader2, Heart, Download, CalendarRange } from "lucide-react";
import { api } from "../lib/api";

const MEAL_TIME_ORDER = ["breakfast", "lunch", "snack", "dinner", "late"];
const MEAL_TIME_LABEL = { breakfast: "Brkfst", lunch: "Lunch", snack: "Snack", dinner: "Dinner", late: "Late" };

export default function Insights() {
  const [weights, setWeights] = useState([]);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthly, setMonthly] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const [w, s] = await Promise.all([api.listWeight(180), api.monthlyStats(30)]);
      setWeights(w);
      setStats(s);
    } catch {
      toast.error("Couldn't load insights");
    }
  };
  useEffect(() => { load(); }, []);

  const runCoach = async () => {
    setLoading(true);
    try { setCoach(await api.weeklyCoach()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Coach unavailable"); }
    finally { setLoading(false); }
  };

  const runMonthly = async () => {
    setMonthLoading(true);
    try { setMonthly(await api.monthlyCoach(30)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Monthly review unavailable"); }
    finally { setMonthLoading(false); }
  };

  const weightChart = weights.map((w) => ({ date: w.date.slice(5), weight: w.weight }));
  const mealTimeChart = stats
    ? MEAL_TIME_ORDER.map((k) => ({ name: MEAL_TIME_LABEL[k], count: stats.by_meal_time?.[k] || 0 }))
    : [];

  return (
    <div className="px-5 pt-8 pb-6 space-y-6" data-testid="insights-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-sage">Insights</p>
        <h1 className="font-heading text-3xl font-extrabold text-charcoal mt-2">Your patterns</h1>
        <p className="text-slate2 mt-2 text-sm leading-relaxed">
          Progress is rarely linear. Small, sustained shifts add up.
        </p>
      </header>

      {/* Weight chart */}
      <section className="card-soft p-5" data-testid="weight-chart">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-charcoal">Weight (kg)</h2>
          <span className="text-xs text-slate2">Last 180 days</span>
        </div>
        {weightChart.length < 2 ? (
          <p className="text-sm text-slate2 mt-4">Log your weight on the Home tab for a few days to see your trend.</p>
        ) : (
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weightChart} margin={{ top: 10, right: 10, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="#F4F1EA" vertical={false} />
                <XAxis dataKey="date" stroke="#59605C" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#59605C" fontSize={11} tickLine={false} axisLine={false} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 8px 30px rgba(44,48,46,0.08)" }} labelStyle={{ color: "#59605C", fontSize: 12 }} />
                <Line type="monotone" dataKey="weight" stroke="#8A9A86" strokeWidth={2.5} dot={{ r: 3, fill: "#8A9A86" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Weekly Coach */}
      <section className="rounded-3xl bg-sand p-6 space-y-4" data-testid="weekly-coach-card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-sage flex items-center justify-center shrink-0">
            <Heart className="text-cream" size={20} strokeWidth={1.8} />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-charcoal">Weekly Coach</h2>
            <p className="text-sm text-slate2 mt-1">A supportive recap of the last 7 days.</p>
          </div>
        </div>

        {!coach ? (
          <button onClick={runCoach} disabled={loading} data-testid="generate-coach-btn"
            className="w-full rounded-2xl bg-charcoal text-cream py-3 font-heading font-bold tap-shrink flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
            {loading ? "Thinking…" : "Generate this week's recap"}
          </button>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage">This week</p>
              <ul className="mt-2 space-y-1.5">
                {coach.this_week.map((t, i) => (<li key={i} className="text-sm text-charcoal flex gap-2"><span className="text-sage">•</span><span>{t}</span></li>))}
              </ul>
            </div>
            {coach.positive_trends?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage">Positive trends</p>
                <ul className="mt-2 space-y-1.5">
                  {coach.positive_trends.map((t, i) => (<li key={i} className="text-sm text-charcoal flex gap-2"><span className="text-sage">•</span><span>{t}</span></li>))}
                </ul>
              </div>
            )}
            {coach.next_step && (
              <div className="rounded-2xl bg-white p-4">
                <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage mb-1">Next step</p>
                <p className="text-sm text-charcoal leading-relaxed" data-testid="coach-next-step">{coach.next_step}</p>
              </div>
            )}
            <button onClick={runCoach} disabled={loading} data-testid="regen-coach-btn"
              className="w-full rounded-2xl bg-white py-2.5 text-sm font-bold text-charcoal tap-shrink">Regenerate</button>
          </div>
        )}
      </section>

      {/* Monthly stats */}
      {stats && (
        <section className="card-soft p-5 space-y-4" data-testid="monthly-stats">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-charcoal">Last 30 days</h2>
            <CalendarRange size={18} className="text-slate2" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Days logged"   value={`${stats.days_logged} / ${stats.days_covered}`} />
            <Stat label="Meals"         value={stats.meals_logged} />
            <Stat label="Drinks"        value={stats.drinks_logged} />
            <Stat label="Treadmill"     value={stats.treadmill_sessions_total} />
            <Stat label="Avg kcal / day" value={stats.avg_daily_calories_low != null ? `${stats.avg_daily_calories_low}–${stats.avg_daily_calories_high}` : "—"} />
            <Stat label="Weight Δ"      value={stats.weight_change_kg != null ? `${stats.weight_change_kg > 0 ? "+" : ""}${stats.weight_change_kg} kg` : "—"} />
          </div>

          {(stats.weekday_avg_calories != null || stats.weekend_avg_calories != null) && (
            <div className="rounded-2xl bg-sand p-4">
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage mb-2">Weekday vs weekend (avg kcal)</p>
              <div className="flex items-center justify-between text-sm text-charcoal">
                <span>Weekdays</span><span className="font-bold">{stats.weekday_avg_calories ?? "—"}</span>
              </div>
              <div className="flex items-center justify-between text-sm text-charcoal mt-1">
                <span>Weekends</span><span className="font-bold">{stats.weekend_avg_calories ?? "—"}</span>
              </div>
            </div>
          )}

          {mealTimeChart.some((d) => d.count > 0) && (
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage mb-2">When you eat</p>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mealTimeChart} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid stroke="#F4F1EA" vertical={false} />
                    <XAxis dataKey="name" stroke="#59605C" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#59605C" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip cursor={{ fill: "#F4F1EA" }} contentStyle={{ borderRadius: 12, border: "none" }} />
                    <Bar dataKey="count" fill="#8A9A86" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {stats.top_meals?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage mb-2">Top meals</p>
              <ul className="space-y-1.5">
                {stats.top_meals.slice(0, 5).map((m, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-charcoal truncate pr-3">{m.name}</span>
                    <span className="text-slate2 font-semibold">{m.count}×</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {stats.top_drinks?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage mb-2">Top drinks</p>
              <ul className="space-y-1.5">
                {stats.top_drinks.slice(0, 5).map((m, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-charcoal truncate pr-3">{m.name}</span>
                    <span className="text-slate2 font-semibold">{m.count}×</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Monthly Coach */}
      <section className="rounded-3xl bg-sage-light p-6 space-y-4" data-testid="monthly-coach-card">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-charcoal flex items-center justify-center shrink-0">
            <CalendarRange className="text-cream" size={20} strokeWidth={1.8} />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-charcoal">Monthly Review</h2>
            <p className="text-sm text-slate2 mt-1">A calm 30-day pattern read-out — perfect for that month-later analysis you wanted.</p>
          </div>
        </div>

        {!monthly ? (
          <button onClick={runMonthly} disabled={monthLoading} data-testid="generate-monthly-btn"
            className="w-full rounded-2xl bg-charcoal text-cream py-3 font-heading font-bold tap-shrink flex items-center justify-center gap-2 disabled:opacity-60">
            {monthLoading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
            {monthLoading ? "Reading 30 days…" : "Generate monthly review"}
          </button>
        ) : (
          <div className="space-y-4">
            {monthly.highlights?.length > 0 && (
              <CoachList title="Highlights" items={monthly.highlights} />
            )}
            {monthly.patterns?.length > 0 && (
              <CoachList title="Patterns" items={monthly.patterns} />
            )}
            {monthly.wins?.length > 0 && (
              <CoachList title="Wins" items={monthly.wins} />
            )}
            {monthly.next_month_focus && (
              <div className="rounded-2xl bg-white p-4">
                <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage mb-1">Next month focus</p>
                <p className="text-sm text-charcoal leading-relaxed" data-testid="monthly-next-focus">{monthly.next_month_focus}</p>
              </div>
            )}
            <button onClick={runMonthly} disabled={monthLoading} data-testid="regen-monthly-btn"
              className="w-full rounded-2xl bg-white py-2.5 text-sm font-bold text-charcoal tap-shrink">Regenerate</button>
          </div>
        )}
      </section>

      {/* Export */}
      <section className="card-soft p-5 flex items-center justify-between gap-4" data-testid="export-card">
        <div>
          <h2 className="font-heading text-lg font-bold text-charcoal">Export your data</h2>
          <p className="text-xs text-slate2 mt-1">JSON dump — feed it to another tool or LLM at month-end.</p>
        </div>
        <a
          href={api.exportUrl()}
          download
          data-testid="export-btn"
          className="rounded-2xl bg-charcoal text-cream px-4 py-3 font-heading font-bold tap-shrink flex items-center gap-2 shrink-0"
        >
          <Download size={16} /> Export
        </a>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-sand p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate2">{label}</p>
      <p className="font-heading text-xl font-extrabold text-charcoal mt-1">{value}</p>
    </div>
  );
}

function CoachList({ title, items }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] font-bold text-sage">{title}</p>
      <ul className="mt-2 space-y-1.5">
        {items.map((t, i) => (
          <li key={i} className="text-sm text-charcoal flex gap-2"><span className="text-sage">•</span><span>{t}</span></li>
        ))}
      </ul>
    </div>
  );
}
